<template>
    <component
        :is="is"
        data-component="AmsListItem"
        class="relative flex flex-col p-16 border shadow cursor-pointer rounded-10 hover:shadow-hover"
        :class="{
            'w-[283px] screen-m:w-[348px] screen-l:w-[281px] screen-xxl:w-[348px]': isVertical,
        }"
        @click="onItemClick"
    >
        <div class="flex flex-col grow" :class="{ 'screen-m:flex-row': !isVertical }">
            <ams-list-item-picture
                :item="item"
                :is-vertical="isVertical"
                class="mb-16 -mx-16 -mt-16"
                :class="{
                    'h-[190px] screen-m:h-[233px] screen-l:h-[192px] screen-xxl:h-[233px]': isVertical,
                    'screen-m:mx-0 screen-m:mt-0 screen-m:mb-0 screen-m:mr-16': !isVertical,
                    'screen-m:h-[144px] screen-l:h-[184px] screen-xxl:h-[162px]': !isVertical,
                    'h-[255px] screen-l:w-[255px] screen-m:w-[200px]': !isVertical,
                }"
            />
            <div class="flex flex-col min-w-0 grow">
                <ams-list-item-header
                    ref="headerRef"
                    :class="hasPriceIndicator ? 'mb-8 screen-m:mb-16' : 'mb-8'"
                    :item="item"
                    :is-vertical="isVertical"
                    :for-location-name="forLocationName"
                    :hasPriceIndicator="hasPriceIndicator"
                />
                <ams-list-item-body
                    class="contents"
                    :item="item"
                    :is-vertical="isVertical"
                    :has-dynamic-tooltip="hasDynamicTooltip"
                    :hideBadges="hasPriceIndicator"
                />
            </div>
        </div>
        <hr class="w-full mt-8 mb-16 screen-l:mt-16" />
        <ams-list-item-footer
            :item="item"
            :hasPriceIndicator="hasPriceIndicator"
            @click-price-indicator="onClickPriceIndicator"
        />

        <json-ld :json="itemJsonLd" />
    </component>
</template>

<script lang="ts" setup>
import AmsListItemPicture from './AmsListItemPicture.vue'
import AmsListItemHeader from './AmsListItemHeader.vue'
import AmsListItemBody from './AmsListItemBody.vue'
import AmsListItemFooter from './AmsListItemFooter.vue'
import JsonLd from '../../components/JsonLd'
import { amsItemJsonLd } from './amsItemJsonLd'
import { AmsItem } from '../../services/amsSearch/amsSearch.interface'
import { PropType, ref } from 'vue'

const props = defineProps({
    item: {
        type: Object as PropType<AmsItem>,
        required: true,
    },
    is: {
        type: String as PropType<keyof HTMLElementTagNameMap>,
        default: 'li',
    },
    isVertical: {
        type: Boolean,
        default: false,
    },
    hasDynamicTooltip: {
        type: Boolean,
        default: false,
    },
    forLocationName: String as PropType<AmsItem['city']>,
    hasPriceIndicator: Boolean,
})

const headerRef = ref<InstanceType<typeof AmsListItemHeader>>()

const emit = defineEmits(['priceIndicatorClick'])

const onClickPriceIndicator = () => {
    emit('priceIndicatorClick', props.item)
}

const onItemClick = (event: PointerEvent) => {
    if (event.target === headerRef.value?.anchorLink) {
        return
    }

    /*            reasoning: we want to open the link on every click
     *             solution: we trigger the click on the link manually
     *          consequence: we have two click events when the click is not on the link
     * consequence-solution: we stop all other clicks to prevent double events on click */
    event.stopImmediatePropagation()
    headerRef.value?.anchorLink?.click()
}

const itemJsonLd = amsItemJsonLd(props.item)
</script>
